import {loadPdfLibrary} from './runtime';

// Print the complete document, independent of the viewer's virtualized pages.
export async function printPdf(pdf, {signal, onProgress = () => {}, rotation = 0} = {}) {
    const frame = document.createElement('iframe');
    frame.title = 'PDF print';
    Object.assign(frame.style, {position: 'fixed', width: '0', height: '0', border: '0'});
    document.body.append(frame);
    const urls = [];
    let task;
    const abort = () => task?.cancel();
    signal?.addEventListener('abort', abort);
    const check = () => { if (signal?.aborted) throw new DOMException('Aborted', 'AbortError'); };
    try {
        const {AnnotationMode} = await loadPdfLibrary();
        check();
        const target = frame.contentDocument;
        const css = target.createElement('style');
        css.textContent = 'body{margin:0}.sheet{break-after:page}.sheet:last-child{break-after:auto}img{display:block;width:100%;height:100%}';
        target.head.append(css);
        const scratch = document.createElement('canvas');
        const optionalContentConfigPromise = pdf.getOptionalContentConfig({intent: 'print'});
        const printAnnotationStorage = pdf.annotationStorage?.print;
        for (let number = 1; number <= pdf.numPages; number++) {
            check();
            const page = await pdf.getPage(number);
            check();
            const natural = page.getViewport({scale: 1, rotation: page.rotate + rotation});
            const scale = Math.min(150 / 72, Math.sqrt(16000000 / (natural.width * natural.height)));
            const viewport = page.getViewport({scale, rotation: page.rotate + rotation});
            scratch.width = viewport.width;
            scratch.height = viewport.height;
            task = page.render({canvas: scratch, canvasContext: scratch.getContext('2d'), viewport,
                intent: 'print', annotationMode: AnnotationMode.ENABLE_STORAGE,
                printAnnotationStorage, optionalContentConfigPromise});
            await task.promise;
            check();
            const blob = await new Promise(resolve => scratch.toBlob(resolve));
            if (!blob) throw new Error('Unable to prepare printed page');
            const url = URL.createObjectURL(blob);
            urls.push(url);
            const sheet = target.createElement('div');
            sheet.className = 'sheet';
            Object.assign(sheet.style, {width: `${natural.width}pt`, height: `${natural.height}pt`, page: `pdfPage${number}`});
            css.textContent += `@page pdfPage${number}{size:${natural.width}pt ${natural.height}pt;margin:0}`;
            const image = target.createElement('img');
            const ready = new Promise((resolve, reject) => {
                const cancelled = () => reject(new DOMException('Aborted', 'AbortError'));
                const finish = callback => () => { signal?.removeEventListener('abort', cancelled); callback(); };
                image.onload = finish(resolve);
                image.onerror = finish(() => reject(new Error('Unable to load printed page')));
                signal?.addEventListener('abort', cancelled, {once: true});
            });
            image.src = url;
            sheet.append(image);
            target.body.append(sheet);
            await ready;
            onProgress(number, pdf.numPages);
        }
        scratch.width = scratch.height = 0;
        check();
        await new Promise((resolve, reject) => {
            const win = frame.contentWindow;
            const finish = () => { signal?.removeEventListener('abort', cancelled); resolve(); };
            const cancelled = () => { win.removeEventListener('afterprint', finish); reject(new DOMException('Aborted', 'AbortError')); };
            win.addEventListener('afterprint', finish, {once: true});
            signal?.addEventListener('abort', cancelled, {once: true});
            win.focus();
            win.print();
        });
    } finally {
        signal?.removeEventListener('abort', abort);
        task?.cancel();
        urls.forEach(url => URL.revokeObjectURL(url));
        frame.remove();
    }
}
